/**
 * Notifications container
 */
import {
  AbstractType,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  InjectionToken,
  Injector,
  OnDestroy,
  OnInit,
  Type,
  ViewEncapsulation,
} from '@angular/core';

import { Toast, ToasterConfig, ToasterService } from 'angular2-toaster';
import { select } from '@angular-redux/store';

import { UiActions } from '@store/actions';
import { DEFAULT_TOAST_TIMEOUTS, THEMES } from '@config/global-constants';
import { Observable, rx, Subscription } from '@shared/rx';
import { XVII18nService } from '@app/i18n/services';
import {
  INotification,
  INotificationsState,
} from '@store/reducers/notifications';
import {
  INotificationBoxItem,
  INotificationBoxItemBase,
  INotificationHTML,
  NotificationContentType,
  NotificationType,
} from '@shared/types';
import { has, isEmpty, negate, isFunction, get } from 'lodash';
import { getTheme } from '@shared/utils';
import { INotificationToast } from './notifications.container.types';
import { NotificationI18nService } from '@app/i18n/services/notification-translate.service';
import { lazyInject } from '@app/containers/base/decorators/lazy-inject.decorator';
import { I18n } from '@app/i18n/strings-keys';

const nonActiveEntities = [];

@Component({
  selector: 'xvi-notifications',
  templateUrl: 'notifications.container.html',
  styleUrls: ['./notifications.container.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class NotificationsContainerComponent implements OnInit, OnDestroy {
  public errorConfig: ToasterConfig = new ToasterConfig({
    timeout: {
      error: DEFAULT_TOAST_TIMEOUTS.ERROR_TIMEOUT,
      success: DEFAULT_TOAST_TIMEOUTS.SUCCESS_TIMEOUT,
      warning: DEFAULT_TOAST_TIMEOUTS.WARNING_TIMEOUT,
      info: DEFAULT_TOAST_TIMEOUTS.INFO_TIMEOUT,
      common: DEFAULT_TOAST_TIMEOUTS.SUCCESS_TIMEOUT,
    },
    typeClasses: {
      error: 'toast-error',
      success: 'toast-success',
      warning: 'toast-warning',
      info: 'toast-info',
      common: 'common-notification',
    },
  });

  @lazyInject(ToasterService)
  private readonly toastService: ToasterService;
  @lazyInject(UiActions)
  private readonly uiActions: UiActions;
  @lazyInject(ChangeDetectorRef)
  private readonly changeDetectorRef: ChangeDetectorRef;
  @lazyInject(XVII18nService)
  private readonly i18nService: XVII18nService;
  @lazyInject(NotificationI18nService)
  private readonly notificationI18nService: NotificationI18nService;

  @select('notifications')
  public notifications$: Observable<INotificationsState>;
  @select(['ui', 'loader']) public loader$: Observable<boolean>;

  public subscription: Subscription;

  public constructor(private injector: Injector) {}

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public ngOnInit(): void {
    if (getTheme() === THEMES.GENIUS) {
      this.initToastNotifications();
    } else {
      this.storeSubscriptionInit();
    }
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private storeSubscriptionInit(): void {
    this.subscription = this.notifications$.subscribe((allNotifications) => {
      const { notifications, latestNotification } = allNotifications;

      if (
        latestNotification &&
        this.isNotificationAllowed(latestNotification.entityType)
      ) {
        this.loader$
          .pipe(
            rx.startWith(true),
            rx.debounceTime(100),
            rx.takeWhile((loader) => loader),
          )
          .subscribe({
            complete: (): void =>
              this.setNotifications(notifications, latestNotification),
          });
      }
    });
  }

  private setNotifications(
    notifications: INotificationToast[],
    latestNotification: INotificationToast,
  ): void {
    const newNotification = notifications.find(
      (notification) => notification.id === latestNotification.id,
    );
    const toast: Toast = this.notificationI18nService.translateNotification({
      ...latestNotification,
    });

    this.toastService.pop(toast);

    if (newNotification) {
      newNotification.toastId = toast.toastId;
    }
    // Don't block the screen on failure. Refreshes within portal frame are not ideal
    if (latestNotification.type === 'error') {
      this.uiActions.disableLoader();
    }

    if (latestNotification.type === 'success') {
      notifications
        .filter(
          (notification) =>
            notification.type === 'error' &&
            notification.entityType === latestNotification.entityType,
        )
        .forEach((notification) => {
          this.toastService.clear(notification.toastId);
        });
    }
    this.changeDetectorRef.markForCheck();
  }

  private isNotificationAllowed(entityType): boolean {
    return !nonActiveEntities.includes(entityType); // eslint-disable-line
  }

  private initToastNotifications(): void {
    const handledIds = new Set<string>();
    this.subscription = this.notifications$
      .pipe(
        rx.switchMap((res) => res.notifications),
        rx.filter(negate(isEmpty)),
        rx.filter((notification) => {
          const id = notification.id || notification.toastId;
          if (!id || handledIds.has(id)) return false;
          handledIds.add(id);

          return true;
        }),
      )
      .subscribe((notification) => {
        const translatedNotification =
          this.notificationI18nService.translateNotification({
            ...notification,
          });

        const mappedNotification = this.mapToastNotificationToNotificationItem(
          translatedNotification as INotification,
        );
        if (isEmpty(mappedNotification)) return;
        this.uiActions.addNotification(mappedNotification);
      });
  }

  private mapToastNotificationToNotificationItem(
    notification: INotification,
  ): INotificationBoxItem {
    const id = notification.id || notification.toastId;
    let toast: INotificationBoxItem;
    switch (notification.type) {
      case 'success':
        toast = this.mapToastSuccessNotificationToNotificationItem(
          id,
          notification,
        );
        break;
      case 'error':
        toast = this.mapToastErrorNotificationToNotificationItem(
          id,
          notification,
        );
        break;
      default:
        toast = this.mapToastDefaultNotificationToNotificationItem(
          id,
          notification,
        );
        break;
    }

    if (notification.timeout) {
      toast.ttl = notification.timeout;
    }

    return toast;
  }

  private getSuccessNotificationBase(id: string): INotificationBoxItemBase {
    return {
      id,
      type: NotificationType.SUCCESS,
      ttl: DEFAULT_TOAST_TIMEOUTS.SUCCESS_TIMEOUT,
      onClick: () => this.uiActions.removeNotification(id),
      showAfter$: this.loader$.pipe(
        rx.startWith(true),
        rx.debounceTime(100),
        rx.takeWhile((loader) => loader),
      ),
    };
  }

  private getErrorNotificationBase(
    id: string,
    options?: Partial<INotificationBoxItemBase>,
  ): INotificationBoxItemBase {
    const closeFunction = get(options, 'closeNotification');
    const onHoverFunction: Function = isFunction(closeFunction)
      ? undefined
      : get(options, 'onHover', () =>
          setTimeout(() => this.uiActions.removeNotification(id), 100),
        );

    return {
      id,
      type: NotificationType.ERROR,
      closeNotification: closeFunction,
      onHover: onHoverFunction,
    };
  }

  private mapToastSuccessNotificationToNotificationItem(
    id: string,
    notification: INotification,
  ): INotificationBoxItem {
    return {
      id,
      ...this.getSuccessNotificationBase(id),
      content: [
        <INotificationHTML>{
          type: NotificationContentType.HTML,
          htmlString: `<b>${this.i18nService.instant(I18n.Common.Success)}:</b> `,
        },
        <INotificationHTML>{
          type: NotificationContentType.HTML,
          htmlString: notification.body ?? notification.title,
        },
      ],
    } as INotificationBoxItem;
  }

  private mapToastErrorNotificationToNotificationItem(
    id: string,
    notification: INotification,
  ): INotificationBoxItem {
    const errorLabel = notification.errorLabel ?? I18n.Common.Failure;

    const contentError = [
      <INotificationHTML>{
        type: NotificationContentType.HTML,
        htmlString: `<b>${this.i18nService.translate(errorLabel)}:</b> `,
      },
    ];
    if (has(notification, 'title')) {
      contentError.push(<INotificationHTML>{
        type: NotificationContentType.HTML,
        htmlString: notification.title,
      });
    }
    if (has(notification, 'body')) {
      contentError.push(<INotificationHTML>{
        type: NotificationContentType.HTML,
        wrapItem: true,
        htmlString: notification.body,
      });
    }

    return {
      id,
      ...this.getErrorNotificationBase(
        id,
        get(notification, 'notificationBoxOptions'),
      ),
      content: contentError,
    } as INotificationBoxItem;
  }

  private mapToastDefaultNotificationToNotificationItem(
    id: string,
    notification: INotification,
  ): INotificationBoxItem {
    const contentDefault = [];
    let wrapItem = false;
    if (has(notification, 'title')) {
      contentDefault.push(<INotificationHTML>{
        type: NotificationContentType.HTML,
        htmlString: notification.title,
      });
      wrapItem = true;
    }
    if (has(notification, 'body')) {
      contentDefault.push(<INotificationHTML>{
        wrapItem,
        type: NotificationContentType.HTML,
        htmlString: notification.body,
      });
    }

    if (notification?.type === NotificationType.COMMON) {
      notification.type = NotificationType.INFO;
    }

    return {
      id,
      content: contentDefault,
      type: notification?.type ?? NotificationType.INFO,
      ttl:
        notification.type === NotificationType.WARNING
          ? DEFAULT_TOAST_TIMEOUTS.WARNING_TIMEOUT
          : DEFAULT_TOAST_TIMEOUTS.INFO_TIMEOUT,
    } as INotificationBoxItem;
  }
}
